import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import { hydrateEditorStores, persistEditorStores } from '../utils/editorPersistence'
import { useEditorOptions } from './editorOptions'
import { type RestorablePlayStore, useEditorRestorePlays } from './editorRestorePlays'

type LotteryStore = RestorablePlayStore & {
  $hydrate?: () => void
  $persist?: () => void
  setUp: (config: Config) => void
}

export const useEditor = <TStore extends LotteryStore>(
  useStore: (lotteryId: LotteryId) => TStore,
) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const { navigateToLocalized } = useRouterUtils()
  const { params } = useRoute()
  const nuxtApp = useNuxtApp()

  const lotteryId = getLotteryIdInCorrectCase(params.lotteryId)

  if (!lotteryId) throw create404Error()

  const store = useStore(lotteryId)
  const options = useEditorOptions(lotteryId)

  const { onError, result } = useLotteryQuery({
    licensedTerritory,
    lotteryId,
  })

  const config = computed(() => result.value?.lottery.currentConfig ?? null)

  // the server resolves these before rendering, the client hydrates from them to match the markup
  const playsReady = useState(`editor-plays-ready-${lotteryId}`, () => false)
  const optionsReady = useState(`editor-options-ready-${lotteryId}`, () => false)
  if (!nuxtApp.isHydrating) playsReady.value = optionsReady.value = false

  const priceList = computed(() => result.value?.lottery.priceList)

  const restore = useEditorRestorePlays(lotteryId, store, options, config)

  const failed = new Promise<void>((resolve) => onError(() => resolve()))
  const mounted = new Promise<void>((resolve) => onMounted(resolve))

  const navigateToHome = async () => {
    const invokeNavigation = () => navigateToLocalized('/', { replace: true })

    if (nuxtApp.isHydrating) return onNuxtReady(invokeNavigation)
    await invokeNavigation()
  }

  const editorIsReady = (async () => {
    await Promise.race([until(config).not.toBeNull(), failed])
    const currentConfig = config.value
    if (!currentConfig) return

    if (import.meta.client) {
      // while hydrating, the stores keep the server state until the markup is in place, see editorPersistence
      if (nuxtApp.isHydrating) await mounted
      hydrateEditorStores(store, options.store)
      restore.prepare(currentConfig)
    }

    store.setUp(currentConfig)
    playsReady.value = true
    options.store.setUp(currentConfig)

    try {
      await options.applyFromQuery()
    } catch {
      // the gate has to open even when applying the params fails
    }

    if (import.meta.client) persistEditorStores(store, options.store)
    optionsReady.value = true
  })()

  if (import.meta.server) onServerPrefetch(() => editorIsReady)
  onError(navigateToHome)

  // plays made in this session are not "previous" ones, leaving with them settles the question
  onScopeDispose(() => {
    if (restore.hasPlays()) restore.markAnswered()
  })

  return {
    config,
    editorIsReady,
    lotteryId,
    navigateToHome,
    optionsReady,
    playsReady,
    priceList,
    store,
  }
}
